import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { EyeOff, Power, Trash2, X } from "lucide-react";
import type { AdminOSImage } from "@/types/admin";

interface BulkActionsToolbarProps {
  selectedImages: AdminOSImage[];
  onActivate: () => void;
  onDeactivate: () => void;
  onDelete: () => void;
  onClearSelection: () => void;
  isLoading?: boolean;
}

type ActionType = "activate" | "deactivate" | "delete" | null;

/**
 * Compact strip shown above the table while rows are ticked. Sized like the error banner that
 * shares the slot, so ticking a row does not push the table down by a whole card.
 */
export function BulkActionsToolbar({
  selectedImages,
  onActivate,
  onDeactivate,
  onDelete,
  onClearSelection,
  isLoading,
}: BulkActionsToolbarProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<ActionType>(null);

  if (selectedImages.length === 0) {
    return null;
  }

  const hasImagesInUse = selectedImages.some((img) => img.usageCount > 0);
  const canDelete = !hasImagesInUse;

  const handleActionClick = (action: ActionType) => {
    setPendingAction(action);
    setDialogOpen(true);
  };

  const handleConfirm = () => {
    switch (pendingAction) {
      case "activate":
        onActivate();
        break;
      case "deactivate":
        onDeactivate();
        break;
      case "delete":
        onDelete();
        break;
    }
    setDialogOpen(false);
    setPendingAction(null);
  };

  const getDialogContent = () => {
    switch (pendingAction) {
      case "activate":
        return {
          title: "Activate OS Images",
          description: `Activate ${selectedImages.length} OS image(s)? They will become available for VM creation.`,
          actionLabel: "Activate",
          variant: "default" as const,
        };
      case "deactivate":
        return {
          title: "Deactivate OS Images",
          description: `Deactivate ${selectedImages.length} OS image(s)? They will no longer be offered for new VMs.`,
          actionLabel: "Deactivate",
          variant: "default" as const,
        };
      case "delete":
        return {
          title: "Delete OS Images",
          description: `Delete ${selectedImages.length} OS image(s)? This action cannot be undone.`,
          actionLabel: "Delete",
          variant: "destructive" as const,
        };
      default:
        return {
          title: "",
          description: "",
          actionLabel: "",
          variant: "default" as const,
        };
    }
  };

  const dialogContent = getDialogContent();

  return (
    <>
      <div className="mb-2 flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">
            {selectedImages.length} selected
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleActionClick("activate")}
            disabled={isLoading}
            className="h-7 gap-1.5 px-2 text-xs"
          >
            <Power className="h-3.5 w-3.5" />
            Activate
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleActionClick("deactivate")}
            disabled={isLoading}
            className="h-7 gap-1.5 px-2 text-xs"
          >
            <EyeOff className="h-3.5 w-3.5" />
            Deactivate
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleActionClick("delete")}
            disabled={isLoading || !canDelete}
            title={
              hasImagesInUse
                ? "Some selected images are used by VMs"
                : undefined
            }
            className="h-7 gap-1.5 px-2 text-xs text-destructive-ink hover:bg-destructive hover:text-destructive-foreground"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClearSelection}
          disabled={isLoading}
          aria-label="Clear selection"
          title="Clear selection"
          className="h-7 w-7 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialogContent.title}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              {dialogContent.description}
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={
                dialogContent.variant === "destructive"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : ""
              }
            >
              {dialogContent.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
